import { forwardRef, type ReactElement, type ReactNode } from 'react'
import {
  Button as RacButton,
  type ButtonProps as RacButtonProps,
} from 'react-aria-components'
import { type DsIconProps, Icon } from '#ui/Icon.tsx'
import { getButtonClasses } from '#utils/getButtonClasses.ts'
import type { Rounding, Size } from '#utils/misc.ts'

type ButtonTheme =
  | 'blank'
  | 'danger'
  | 'hollow'
  | 'primary'
  | 'secondary'
  | 'subtle'
  | 'subtleFlag'
  | 'subtleOk'
  | 'subtleWarn'
type ButtonWidth = 'auto' | 'full'
export type BaseButtonProps = {
  centerLoader?: boolean
  className?: string
  icon?: ReactElement<DsIconProps>
  isBusy?: boolean
  onClick?: RacButtonProps['onPress'] // For compatibility
  rounding?: Rounding
  size?: Size
  theme?: ButtonTheme
  title?: string
  width?: ButtonWidth
}
export type ButtonProps = BaseButtonProps &
  Omit<RacButtonProps, 'className'> & {
    children?: ReactNode
  }
interface IconWrapProps
  extends Required<Pick<ButtonProps, 'centerLoader' | 'isBusy'>> {
  children: ReactNode
}

function IconWrap({ centerLoader, children, isBusy }: IconWrapProps) {
  if (centerLoader && isBusy) {
    return <span className='invisible'>{children}</span>
  }
  return children
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      centerLoader = false,
      children,
      icon,
      isBusy = false,
      onClick,
      rounding = 'all',
      size = 'md',
      theme = 'secondary',
      width = 'auto',
      // React-aria default props:
      className,
      isDisabled = false,
      onPress,
      type = 'submit',
      ...props
    },
    forwardRef
  ) {
    return (
      <RacButton
        {...props}
        className={getButtonClasses({
          className,
          rounding,
          size,
          theme,
          width,
        })}
        isDisabled={isDisabled || isBusy}
        onPress={onPress || onClick}
        ref={forwardRef}
        type={type}
      >
        <IconWrap centerLoader={centerLoader} isBusy={isBusy}>
          {children}
        </IconWrap>
        {isBusy ? (
          <Icon
            className={centerLoader ? 'absolute inset-0 m-auto' : undefined}
            name='loader'
            size='16'
          />
        ) : (
          icon
        )}
      </RacButton>
    )
  }
)
